import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  getApplication,
  getRegistrationsForApplication,
  registerApplication,
  resetSelectedRegistration,
  getLevelsOfAssurance,
  addRole,
  resolveIdentity,
  resolveApplicationManager,
  deleteRegistration,
  regenerateSecret,
  selectRegistrationForEdit,
  updateRegistration,
} from "../../actions";
import { Button, Alert, Tooltip, Modal } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import {
  getActiveApplication,
  isLoading,
  getGroups,
  getAllLevelsOfAssurance,
  userHasRole,
  userId,
  getAllProviders,
} from "../../selectors";
import {
  FullPageLoader,
  ContentTable,
} from "@authzsvc/common-react-components";
import RegeneratedSecret from "./RegeneratedSecret";
import ViewSecretModal from "./ViewSecretModal";
import RegistrationDetails from "./RegistrationDetails";
import { RegistrationProtocols } from "../../models/registrationProtocols";
import { LevelsOfAssurance } from "../../models/levelsOfAssurance";
import SSOModal from "./SSOModal";
import * as cfg from "../../config";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../utils";
import { Link } from "react-router-dom";

class Registrations extends React.Component {
  static propTypes = {
    loadingRegistrations: PropTypes.bool,
    loadingApp: PropTypes.bool,
    loadingOwner: PropTypes.bool,
    application: PropTypes.object,
    active_application: PropTypes.object,
    getApplication: PropTypes.func,
    getRegistrationsForApplication: PropTypes.func,
    registerApplication: PropTypes.func,
    resetSelectedRegistration: PropTypes.func,
    resolveIdentity: PropTypes.func,
    resolveApplicationManager: PropTypes.func,
    getLevelsOfAssurance: PropTypes.func,
    addRole: PropTypes.func,
    currentUser: PropTypes.string,
    loa_levels: PropTypes.array,
    providers: PropTypes.array,
    deleteRegistration: PropTypes.func,
    regenerateSecret: PropTypes.func,
    selectRegistrationForEdit: PropTypes.func,
    updateRegistration: PropTypes.func,
  };

  state = {
    roleModalOpen: false,
    redirect: false,
    owner: {},
    manager: {},
    disabledActions: false,
    isOwnerApplication: false,
    modalOpen: false,
    selectedRegistration: null,
    isEditMode: false,
  };

  isManagedApplication = () =>
    this.state.manager && this.state.manager.type === "Application";

  handleSsoOpen = () => {
    this.setState({
      modalOpen: true,
      isEditMode: false,
      selectedRegistration: null,
    });
  };

  handleSsoClose = () => {
    this.setState({
      modalOpen: false,
      selectedRegistration: null,
      isEditMode: false,
    });
    this.props.resetSelectedRegistration();
  };

  async componentDidMount() {
    const { application, currentUser } = this.props;
    const id = application.id;

    const owner_req = await this.props.resolveIdentity(application.ownerId);
    const owner = owner_req.payload?.data || {};
    const isOwnerApplication =
      owner.upn === currentUser && application?.managerId;

    this.setState({
      owner,
      disabledActions: isOwnerApplication,
      isOwnerApplication,
    });

    const managerId = application?.managerId;
    if (managerId) {
      const managerResponse =
        await this.props.resolveApplicationManager(managerId);
      const manager = managerResponse.payload?.data;
      if (manager) this.setState({ manager });
      if (manager?.type === "Application") {
        this.setState({ disabledActions: true });
      }
    }

    this.props.getRegistrationsForApplication(id);
    this.props.getLevelsOfAssurance();
  }

  handleAddRegistration = async (applicationId, providerId, data) => {
    const resp = await this.props.registerApplication(
      applicationId,
      providerId,
      data,
    );
    if (resp?.type === errorAction(at.REGISTER_APPLICATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await this.handleAddDefaultRole(applicationId);
    this.handleSsoClose();
  };

  handleAddDefaultRole = async (applicationid) => {
    const default_loa = this.props.loa_levels.find(
      (e) => e.value === LevelsOfAssurance.CERN,
    );
    const { active_application } = this.props;
    if (
      active_application &&
      !active_application.roles.find((r) => r.name === cfg.default_role_name)
    ) {
      const resp = await this.props.addRole({
        minimumLoaId: default_loa?.id,
        applyToAllUsers: true,
        name: cfg.default_role_name,
        description: "Users must be from CERN to have access",
        displayName: "Default Allowed Users",
        required: true,
        applicationId: applicationid,
      });
      if (resp?.type === errorAction(at.ADD_ROLE)) {
        showErrorNotification(resp.payload.title, resp.payload.data.message);
      }
    }
  };

  handleDelete = (registrationId) => {
    this.props.deleteRegistration(registrationId);
  };

  handleRegenerate = (registration) => {
    if (registration.registration.authenticationProviderId) {
      this.props.regenerateSecret(
        registration.registrationId,
        registration.registration.authenticationProviderId,
      );
    }
  };

  handleEdit = (registration) => {
    this.props.selectRegistrationForEdit(registration);
    this.setState({
      selectedRegistration: registration,
      modalOpen: true,
      isEditMode: true,
    });
  };

  handleEditRegistration = async (applicationId, providerId, data) => {
    const resp = await this.props.updateRegistration(data.id, data);
    if (resp.type === errorAction(at.UPDATE_REGISTRATION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await this.props.getRegistrationsForApplication(applicationId);
    this.setState({ modalOpen: false });
  };

  render() {
    const {
      loadingRegistrations,
      loadingApp,
      loadingOwner,
      active_application,
    } = this.props;
    const loading = loadingRegistrations || loadingApp || loadingOwner;
    const { application, new_registration, registrations } = active_application;

    const { disabledActions } = this.state;

    const columns = [
      {
        title: "Provider",
        dataIndex: "providerDisplayName",
        key: "provider",
      },
      {
        title: "Description",
        dataIndex: "providerDescription",
        key: "description",
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, registration) => (
          <>
            <RegeneratedSecret client={registration.registration.clientId} />
            {registration.providerIdentifier === RegistrationProtocols.OIDC &&
              registration.registration.publicClient === false && (
                <ViewSecretModal
                  disabled={disabledActions}
                  handleRegenerate={() => this.handleRegenerate(registration)}
                  registrationId={registration.registration.id}
                />
              )}
            <Tooltip title="Edit registration">
              <Button
                color="blue"
                variant="filled"
                icon={<EditOutlined />}
                disabled={disabledActions}
                onClick={() => this.handleEdit(registration)}
                style={{ marginRight: 8 }}
              />
            </Tooltip>
            {registration.providerIdentifier === RegistrationProtocols.OIDC && (
              <Link
                to={`/token-exchange/${application.id}/${registration.registrationId}`}
              >
                <Tooltip title="Token exchange permissions">
                  <Button
                    color="green"
                    variant="filled"
                    icon={<LockOutlined />}
                    disabled={disabledActions}
                    style={{ marginRight: 8 }}
                  />
                </Tooltip>
              </Link>
            )}
            <Tooltip title="Delete registration">
              <Button
                icon={<DeleteOutlined />}
                danger
                color="red"
                variant="filled"
                disabled={disabledActions}
                onClick={() =>
                  Modal.confirm({
                    title: "Delete Registration",
                    content: `Are you sure you want to delete the ${registration.providerDisplayName} registration?`,
                    okText: "Delete",
                    cancelText: "Cancel",
                    okButtonProps: { danger: true },
                    onOk: () => this.handleDelete(registration.registrationId),
                  })
                }
              />
            </Tooltip>
          </>
        ),
      },
    ];
    return (
      <>
        {new_registration?.registrationId && <RegistrationDetails />}

        {loading || !application.displayName ? (
          <FullPageLoader />
        ) : (
          <>
            {this.state.disabledActions && (
              <Alert
                type="warning"
                message="Owners of managed applications cannot edit the SSO registration!"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            <ContentTable
              dataSource={registrations || []}
              rowKey="registrationId"
              loading={loading}
              columns={columns}
              locale={{
                emptyText:
                  "There are no SSO registrations for this application yet.",
              }}
              pagination={false}
              footer={
                !this.state.disabledActions
                  ? () => (
                      <div style={{ textAlign: "right" }}>
                        <Button
                          id="ssoButton"
                          type="default"
                          icon={<PlusOutlined />}
                          onClick={this.handleSsoOpen}
                        >
                          Add SSO Registration
                        </Button>
                      </div>
                    )
                  : null
              }
            />

            <SSOModal
              isEdit={this.state.isEditMode}
              application={application}
              onClose={this.handleSsoClose}
              open={this.state.modalOpen}
              handleSubmit={(applicationId, providerId, data) =>
                this.state.isEditMode
                  ? this.handleEditRegistration(applicationId, providerId, data)
                  : this.handleAddRegistration(applicationId, providerId, data)
              }
            />

            {this.isManagedApplication() && (
              <Alert
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message="Applications managed by other applications cannot be modified or deleted!"
                description="Please proceed to the site or service where this application is managed in order to edit or delete it."
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    active_application: getActiveApplication(state),
    groups: getGroups(state),
    loadingApp: isLoading(state, "getApplication"),
    loadingRegistrations: isLoading(state, "getRegistrations"),
    loadingOwner: isLoading(state, "resolveIdentity"),
    loa_levels: getAllLevelsOfAssurance(state),
    admin: userHasRole(state, "admin"),
    currentUser: userId(state),
    providers: getAllProviders(state),
  }),
  {
    getApplication,
    getRegistrationsForApplication,
    registerApplication,
    resetSelectedRegistration,
    getLevelsOfAssurance,
    addRole,
    resolveIdentity,
    resolveApplicationManager,
    deleteRegistration,
    regenerateSecret,
    selectRegistrationForEdit,
    updateRegistration,
  },
)(Registrations);
